import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Building2, Plus, Search, MoreHorizontal, Pencil, Power, Trash2, Users, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
  sitesApi, SiteSummary, ListSitesQuery, SiteStatus, GeofenceMode,
} from "@/lib/sites-api";
import { ApiError } from "@/lib/api";
import { formatDateIn } from "@/lib/datetime";
import { useTenantTimezone } from "@/hooks/useTenantTimezone";

const MODE_OPTIONS: { value: GeofenceMode; label: string }[] = [
  { value: "GPS_AND_GEOFENCE", label: "GPS + Geofence" },
  { value: "GPS_ONLY", label: "GPS Only" },
  { value: "MANUAL", label: "Manual" },
];

const STATUS_OPTIONS: { value: SiteStatus; label: string }[] = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
];

function modeLabel(mode: GeofenceMode): string {
  return MODE_OPTIONS.find((m) => m.value === mode)?.label ?? mode;
}

const Sites = () => {
  const tz = useTenantTimezone();
  const navigate = useNavigate();
  const [sites, setSites] = useState<SiteSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | SiteStatus>("all");
  const [modeFilter, setModeFilter] = useState<"all" | GeofenceMode>("all");

  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<SiteSummary | null>(null);
  const [deletingSubmitting, setDeletingSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const q: ListSitesQuery = {
      pageSize: 200,
      search: search || undefined,
      status: statusFilter === "all" ? undefined : statusFilter,
      geofenceMode: modeFilter === "all" ? undefined : modeFilter,
    };
    sitesApi
      .list(q)
      .then((r) => { if (!cancelled) setSites(r.data); })
      .catch((e) => {
        if (cancelled) return;
        const msg = e instanceof ApiError ? e.message : "Failed to load sites";
        toast.error(msg);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [search, statusFilter, modeFilter, reloadKey]);

  const kpis = useMemo(() => {
    const total = sites.length;
    const active = sites.filter((s) => s.status === "ACTIVE").length;
    const geofenced = sites.filter((s) => s.geofenceMode === "GPS_AND_GEOFENCE").length;
    // Only sites with a radius of their own. Averaging in inherited values
    // would report a figure no site actually has.
    const own = sites.filter((s) => s.geofenceRadiusM != null);
    const avgRadius = own.length
      ? Math.round(own.reduce((a, s) => a + (s.geofenceRadiusM ?? 0), 0) / own.length)
      : 0;
    return { total, active, geofenced, avgRadius };
  }, [sites]);

  const triggerReload = () => setReloadKey((k) => k + 1);

  const toggleStatus = async (s: SiteSummary) => {
    setTogglingId(s.id);
    const next: SiteStatus = s.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    try {
      await sitesApi.update(s.id, { status: next });
      toast.success(`Site ${next === "ACTIVE" ? "activated" : "disabled"}`);
      triggerReload();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Failed to update status");
    } finally {
      setTogglingId(null);
    }
  };

  const submitDelete = async () => {
    if (!deleting) return;
    setDeletingSubmitting(true);
    try {
      await sitesApi.remove(deleting.id);
      toast.success(`Site ${deleting.name} deleted`);
      setDeleting(null);
      triggerReload();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Failed to delete site");
    } finally {
      setDeletingSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-3 sm:grid-cols-4">
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Total sites</div>
          <div className="mt-1 text-2xl font-semibold">{kpis.total}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Active</div>
          <div className="mt-1 text-2xl font-semibold text-success">{kpis.active}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Geofenced</div>
          <div className="mt-1 text-2xl font-semibold">{kpis.geofenced}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Avg radius</div>
          <div className="mt-1 text-2xl font-semibold">{kpis.avgRadius}m</div>
        </Card>
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[240px]">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, code or address…"
                className="pl-9 h-9"
              />
            </div>
          </div>
          <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as "all" | SiteStatus)}>
            <SelectTrigger className="h-9 w-[140px]"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={modeFilter} onValueChange={(v) => setModeFilter(v as "all" | GeofenceMode)}>
            <SelectTrigger className="h-9 w-[180px]"><SelectValue placeholder="Geofence mode" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All modes</SelectItem>
              {MODE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" className="bg-gradient-primary" asChild>
            <Link to="/sites/new"><Plus className="mr-1.5 h-3.5 w-3.5" />Add Site</Link>
          </Button>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
                <th className="p-3 text-left font-medium">Site</th>
                <th className="p-3 text-left font-medium">Code</th>
                <th className="p-3 text-left font-medium">Address</th>
                <th className="p-3 text-left font-medium">Geofence</th>
                <th className="p-3 text-left font-medium">Mode</th>
                <th className="p-3 text-left font-medium">Employees</th>
                <th className="p-3 text-left font-medium">Status</th>
                <th className="p-3 text-left font-medium">Updated</th>
                <th className="p-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && sites.length === 0 && (
                <tr><td colSpan={9} className="p-6 text-center text-muted-foreground">
                  <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                </td></tr>
              )}
              {!loading && sites.length === 0 && (
                <tr><td colSpan={9} className="p-6 text-center text-muted-foreground">No sites found</td></tr>
              )}
              {sites.map((s) => (
                <tr
                  key={s.id}
                  className="border-b last:border-0 hover:bg-secondary/30 cursor-pointer"
                  onClick={() => navigate(`/sites/${s.id}/edit`)}
                >
                  <td className="p-3">
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Building2 className="h-4 w-4" />
                      </div>
                      <span className="font-medium">{s.name}</span>
                    </div>
                  </td>
                  <td className="p-3 font-mono text-xs text-muted-foreground">{s.code}</td>
                  <td className="p-3 text-muted-foreground text-xs max-w-[260px] truncate">{s.address ?? "—"}</td>
                  <td className="p-3">
                    <Badge variant="outline">
                      {s.geofenceRadiusM == null ? "Inherited" : `${s.geofenceRadiusM}m`}
                    </Badge>
                  </td>
                  <td className="p-3 text-xs">{modeLabel(s.geofenceMode)}</td>
                  <td className="p-3">
                    <div className="flex items-center gap-1.5 text-sm">
                      <Users className="h-3 w-3 text-muted-foreground" /> {s.assignedEmployees}
                    </div>
                  </td>
                  <td className="p-3">
                    <Badge
                      variant="outline"
                      className={s.status === "ACTIVE"
                        ? "border-success/30 text-success bg-success/5"
                        : "border-border text-muted-foreground"}
                    >
                      <span className={`mr-1.5 h-1.5 w-1.5 rounded-full ${s.status === "ACTIVE" ? "bg-success" : "bg-muted-foreground"}`} />
                      {s.status === "ACTIVE" ? "Active" : "Inactive"}
                    </Badge>
                  </td>
                  <td className="p-3 text-xs text-muted-foreground">{formatDateIn(s.updatedAt, tz)}</td>
                  <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" disabled={togglingId === s.id}>
                          {togglingId === s.id
                            ? <Loader2 className="h-4 w-4 animate-spin" />
                            : <MoreHorizontal className="h-4 w-4" />}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => navigate(`/sites/${s.id}/edit`)}>
                          <Pencil className="mr-2 h-4 w-4" />Edit site
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => toggleStatus(s)}>
                          <Power className="mr-2 h-4 w-4" />
                          {s.status === "ACTIVE" ? "Disable site" : "Reactivate site"}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => setDeleting(s)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />Delete site
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !deletingSubmitting && !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting && deleting.assignedEmployees > 0 ? (
                <>This site has <span className="font-semibold">{deleting.assignedEmployees}</span> employee(s) assigned.
                Reassign them first, or disable the site instead of deleting.</>
              ) : (
                <>This permanently removes the site. Attendance records stay intact but will no longer link to a named site.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingSubmitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={submitDelete}
              disabled={deletingSubmitting || (deleting?.assignedEmployees ?? 0) > 0}
            >
              {deletingSubmitting ? <><Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />Deleting…</> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Sites;
